/**
 * Platform Help assistant (admin / teacher) — streaming SSE, same wire format as Study Buddy.
 */

export const streamPlatformHelpMessage = async (
  message,
  threadId,
  pageContext,
  userDetails,
  onChunk,
  onComplete,
  onError,
  options = {}
) => {
  const { signal: optSignal, onLinks } = options;
  const controller = optSignal ? null : new AbortController();
  const signal = optSignal || controller?.signal;

  try {
    const auth = localStorage.getItem('auth');
    if (!auth) throw new Error('Authentication token not found. Please log in again.');

    const response = await fetch('/api/v0.2/platform_help/stream_message', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': auth,
      },
      body: JSON.stringify({ message, threadId, pageContext, userDetails }),
      signal,
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    if (!response.body) throw new Error('ReadableStream not supported');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let full = '';

    outer: while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buf += decoder.decode(value, { stream: true });
      const frames = buf.split('\n\n');
      buf = frames.pop() || '';

      for (const frame of frames) {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;

        const data = dataLine.replace(/^data:\s?/, '').trim();
        if (!data) continue;

        if (data === '[DONE]') {
          try {
            await reader.cancel();
          } catch {
            /* ignore */
          }
          break outer;
        }

        let json;
        try {
          json = JSON.parse(data);
        } catch {
          continue;
        }

        const incoming = typeof json.text === 'string' ? json.text : '';
        const isDelta = json.type === 'delta';
        const isSnapshot = json.type === 'snapshot';
        const isLinks = json.type === 'links';

        if (isLinks && Array.isArray(json.urls)) {
          onLinks?.(json.urls);
          continue;
        }

        if (isDelta) {
          if (incoming) {
            full += incoming;
            onChunk?.(incoming, full, json);
          }
        } else if (isSnapshot) {
          if (incoming) {
            const previous = full;
            let delta = '';
            if (incoming.length > full.length && incoming.startsWith(full)) {
              delta = incoming.slice(full.length);
            }
            full = incoming;
            if (delta) onChunk?.(delta, full, json);
            else if (previous !== full) onChunk?.('', full, json);
          }
        }
      }
    }

    onComplete?.(full);
    return full;
  } catch (error) {
    console.error('Platform Help streaming error:', error);
    onError?.(error);
    throw error;
  }
};

export const resetPlatformHelpThread = async (threadId) => {
  const auth = localStorage.getItem('auth');
  if (!auth) throw new Error('Authentication token not found.');

  const response = await fetch('/api/v0.2/platform_help/reset_thread', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-access-token': auth,
    },
    body: JSON.stringify({ threadId: threadId || undefined }),
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};
